import IntentLandingLayout from "@/components/landing/IntentLandingLayout";

export default function AgentVocalIaImmobilier() {
  return (
    <IntentLandingLayout
      canonical="/agent-vocal-ia-immobilier"
      title="Agent vocal IA immobilier pour agence | Immovox"
      description="Agent vocal IA immobilier : Immovox répond aux appels entrants, comprend la demande, qualifie les prospects, prend rendez-vous ou prépare le rappel."
      eyebrow="Agent vocal IA immobilier"
      h1="L'agent vocal IA immobilier qui répond, comprend et agit."
      lede="Immovox tient une conversation téléphonique naturelle avec vos appelants, adapte ses questions au contexte immobilier et déclenche la suite prévue par votre agence."
      serviceType="Agent vocal IA pour l'immobilier"
      primaryCtaLabel="Tester l'agent vocal"
      acquisitionHero={{
        visual: "agent",
        visualLabel: "Agent vocal IA immobilier",
        visualTitle: "De la conversation à l'action.",
        visualText: "L'agent ne se contente pas de prendre un message : il comprend le motif, collecte le contexte utile et suit vos règles.",
        showPhoneDemo: true,
        strip: ["Conversation vocale en langage naturel", "Qualification adaptée à l'immobilier", "Rendez-vous, transfert ou rappel selon vos règles"],
      }}
      definition={<>Un <strong>agent vocal IA immobilier</strong> est un logiciel conversationnel capable de prendre un appel téléphonique, comprendre ce que dit l'appelant et exécuter un scénario défini par l'agence. Il peut qualifier une demande, recueillir des informations, proposer un rendez-vous, transférer un appel ou préparer un rappel.</>}
      sections={[
        {
          title: "Ce qui distingue un agent vocal IA d'un simple répondeur",
          intro: "Le point clé est la conversation : l'appelant s'exprime avec ses propres mots et l'agent adapte la suite de l'échange.",
          bullets: [
            "Comprendre une demande formulée librement plutôt qu'imposer un menu à touches.",
            "Poser une question différente selon qu'il s'agit d'une vente, d'un achat, d'une location ou d'une demande de gestion.",
            "Reformuler ou demander une précision lorsqu'une information manque.",
            "Conserver le contexte de la conversation jusqu'à la fin de l'appel.",
            "Déclencher une action prévue : synthèse, rendez-vous, transfert ou rappel.",
          ],
        },
        {
          title: "Les cas d'usage d'un agent vocal IA en agence immobilière",
          bullets: [
            "Vendeur : identifier une demande d'estimation ou un projet de vente et recueillir le contexte du bien.",
            "Acquéreur : reconnaître le bien ou la recherche, recueillir budget, secteur et disponibilité.",
            "Location : traiter le volume d'appels autour d'une annonce et collecter les éléments prévus par l'agence.",
            "Gestion : identifier le bien ou le dossier, le motif de l'appel et le niveau d'urgence à transmettre.",
            "Hors horaires ou débordement : maintenir une réponse lorsque l'équipe est en visite, en rendez-vous ou déjà en ligne.",
          ],
        },
        {
          title: "Entrant ou sortant : deux usages à ne pas confondre",
          paragraphs: [
            "Cette page concerne principalement l'agent vocal IA qui répond aux appels entrants d'une agence. Il prend le relais lorsque l'appelant contacte volontairement votre numéro.",
            "Les campagnes d'appels sortants automatisés répondent à une logique différente : prospection, relance, consentement et règles de démarchage ne doivent pas être mélangés avec l'accueil des appels entrants.",
          ],
        },
        {
          title: "Comment l'agent vocal s'intègre à votre téléphonie",
          bullets: [
            "Votre numéro public peut rester inchangé.",
            "Un renvoi sur non-réponse peut laisser d'abord votre équipe décrocher.",
            "Un renvoi horaire peut activer l'agent uniquement lorsque l'agence est fermée.",
            "Les appels peuvent être transférés vers un collaborateur lorsque votre scénario le prévoit.",
            "Après l'échange, l'équipe récupère la synthèse et les informations collectées.",
          ],
        },
        {
          title: "Ce qu'il faut tester avant de choisir un agent vocal IA",
          bullets: [
            "La qualité de compréhension sur de vraies formulations d'appelants, pas uniquement une démo scriptée.",
            "La capacité à rester dans le périmètre métier défini et à passer la main lorsqu'une demande le dépasse.",
            "Le niveau de personnalisation des scénarios vendeur, acquéreur, location et gestion.",
            "La gestion des rendez-vous, transferts et notifications réellement nécessaires à votre agence.",
            "La simplicité du renvoi d'appel et la possibilité de tester sans modifier votre numéro public.",
            "La qualité de la synthèse transmise après l'appel.",
          ],
        },
      ]}
      comparison={{
        title: "Agent vocal IA, standard IA ou permanence : quelle différence ?",
        intro: "Les termes se recouvrent parfois, mais ils ne décrivent pas exactement le même besoin.",
        headers: ["Terme", "Ce qu'il décrit", "Quand le rechercher"],
        rows: [
          ["Agent vocal IA", "La technologie conversationnelle qui parle et agit pendant l'appel", "Comprendre les capacités de l'IA vocale"],
          ["Standard téléphonique IA", "L'organisation des appels entrants autour de cette technologie", "Équiper le point d'entrée téléphonique de l'agence"],
          ["Permanence téléphonique", "Le relais lorsque l'équipe habituelle n'est pas disponible", "Couvrir débordement et hors horaires"],
          ["Accueil téléphonique", "La qualité du premier échange et de l'orientation", "Structurer la prise en charge des appelants"],
        ],
      }}
      faq={[
        { question: "Qu'est-ce qu'un agent vocal IA immobilier ?", answer: "C'est un assistant conversationnel qui répond au téléphone pour une agence immobilière, comprend le motif de l'appel et suit un scénario défini : qualification, rendez-vous, transfert ou préparation d'un rappel." },
        { question: "Quelle différence avec un voicebot ou un callbot ?", answer: "Les termes voicebot, callbot et agent vocal IA sont souvent utilisés pour des technologies proches. Le terme agent vocal met généralement l'accent sur la capacité à comprendre le contexte et à effectuer des actions pendant l'échange." },
        { question: "Peut-il prendre des rendez-vous ?", answer: "Oui, lorsque l'agenda et le scénario de l'agence sont configurés pour cela. L'agent peut proposer un créneau ou préparer une demande de rendez-vous selon le fonctionnement choisi." },
        { question: "Peut-il transférer un appel à un négociateur ?", answer: "Oui, des règles de transfert peuvent être prévues pour certains motifs ou certaines situations. L'agence définit les cas où l'humain doit reprendre immédiatement." },
        { question: "Faut-il changer de numéro ?", answer: "Non. L'agent vocal peut être utilisé derrière un renvoi d'appel depuis le numéro actuel de l'agence." },
        { question: "L'agent répond-il aux questions juridiques ou de négociation ?", answer: "Il doit rester dans le périmètre configuré. Lorsqu'une demande dépasse ce périmètre, la bonne pratique consiste à transmettre ou préparer un rappel plutôt qu'improviser une réponse." },
      ]}
      related={[
        { to: "/standard-telephonique-immobilier", label: "Standard téléphonique immobilier", description: "Organiser les appels entrants autour d'un standard IA." },
        { to: "/qualification-appels-immobilier", label: "Qualification des appels", description: "Définir les questions utiles selon le type de prospect." },
        { to: "/telephonie-agence-immobiliere", label: "Téléphonie d'agence", description: "Comprendre numéro, VoIP, renvoi et intégration de l'IA." },
      ]}
      ctaTitle="Tester un agent vocal IA sur vos appels immobiliers"
      ctaText="Appelez la démo, puis configurez vos propres scénarios vendeur, acquéreur, location ou gestion avant d'activer le relais sur votre ligne."
    />
  );
}
